import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Edit2, Check, X, Loader2, Link2, User } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ROLE_LABELS = { admin: "Administrator", student: "Student", faculty: "Faculty", parent: "Parent", hr_manager: "HR Manager", placement_officer: "Placement Officer" };

export default function Profile() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [childEmail, setChildEmail] = useState("");
  const [child, setChild] = useState(null);
  const [linking, setLinking] = useState(false);
  const [linkMsg, setLinkMsg] = useState("");

  useEffect(() => { fetchProfile(); }, []);
  useEffect(() => { if (profile?.role === "parent" || profile?.linked_child_email) fetchChild(); }, [profile]);

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${API}/profile`, { withCredentials: true });
      setProfile(data); setForm(data);
    } catch {}
  };

  const fetchChild = async () => {
    try {
      const { data } = await axios.get(`${API}/profile/child`, { withCredentials: true });
      setChild(data);
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/profile`, {
        name: form.name, phone: form.phone, department: form.department,
        bio: form.bio, designation: form.designation,
        roll_number: form.roll_number, program: form.program,
        semester: form.semester ? parseInt(form.semester) : undefined,
      }, { withCredentials: true });
      setProfile(data);
      setUser(prev => ({ ...prev, name: data.name, phone: data.phone, department: data.department }));
      setEditing(false);
    } catch {} finally { setSaving(false); }
  };

  const handleLinkChild = async () => {
    if (!childEmail) return;
    setLinking(true); setLinkMsg("");
    try {
      const { data } = await axios.post(`${API}/profile/link-child`, { child_email: childEmail }, { withCredentials: true });
      setLinkMsg(`Linked: ${data.child_name}`);
      setChildEmail("");
      fetchProfile(); fetchChild();
    } catch (e) { setLinkMsg(e.response?.data?.detail || "Not found"); }
    finally { setLinking(false); }
  };

  if (!profile) return <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>;

  const roleSpecificFields = {
    student: [{ key: "roll_number", label: "Roll Number" }, { key: "program", label: "Program" }, { key: "semester", label: "Semester" }],
    faculty: [{ key: "designation", label: "Designation" }, { key: "department", label: "Department" }, { key: "bio", label: "Research Areas" }],
    hr_manager: [{ key: "designation", label: "Designation" }, { key: "department", label: "Department" }],
    placement_officer: [{ key: "designation", label: "Designation" }, { key: "department", label: "Department" }],
    admin: [{ key: "designation", label: "Designation" }, { key: "department", label: "Department" }],
    parent: [],
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5" data-testid="profile-page">
      {/* Hero header */}
      <div className="bg-[#0C0C10] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 bg-[#EAFF00] flex items-center justify-center flex-shrink-0">
            <span className="font-['Instrument_Serif'] text-4xl text-[#0C0C10]">{(profile.name || "U").charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.3em] text-[#EAFF00] mb-1">{ROLE_LABELS[profile.role] || profile.role}</p>
            <h2 className="font-['Instrument_Serif'] text-3xl text-[#EEEADF] leading-tight">{profile.name}</h2>
            <p className="font-['DM_Sans'] text-sm text-[#EEEADF]/40 mt-1">{profile.email}</p>
            {profile.department && <p className="font-['DM_Sans'] text-xs text-[#EEEADF]/30 mt-0.5">{profile.department}</p>}
          </div>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} data-testid="edit-profile-btn"
            className="flex items-center gap-2 border border-[#EEEADF]/20 text-[#EEEADF]/60 font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:border-[#EAFF00] hover:text-[#EAFF00] transition-all">
            <Edit2 className="w-3.5 h-3.5" /> Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} data-testid="save-profile-btn"
              className="flex items-center gap-2 bg-[#EAFF00] text-[#0C0C10] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-white transition-all disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Save</>}
            </button>
            <button onClick={() => { setEditing(false); setForm(profile); }} className="border border-[#EEEADF]/20 text-[#EEEADF]/60 px-4 py-3 hover:text-[#EEEADF] transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Personal Info */}
        <div className="bg-white border border-[#E2DDD4] p-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-5">Personal Information</p>
          <div className="space-y-5">
            {[{ key: "name", label: "Full Name" }, { key: "email", label: "Email", readonly: true }, { key: "phone", label: "Phone Number" }].map(({ key, label, readonly }) => (
              <div key={key}>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#BDBAA8] mb-1.5">{label}</label>
                {editing && !readonly ? (
                  <input value={form[key] || ""} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
                ) : (
                  <p className="font-['DM_Sans'] text-sm text-[#0C0C10] pb-2 border-b border-[#F0ECE4]">{profile[key] || <span className="text-[#BDBAA8]">Not set</span>}</p>
                )}
              </div>
            ))}
            <div>
              <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#BDBAA8] mb-1.5">Account Status</label>
              <span className="font-['Syne'] font-bold text-[9px] uppercase px-3 py-1.5 border border-[#22C55E]/30 text-[#16A34A] bg-[#22C55E]/8">Active</span>
            </div>
          </div>
        </div>

        {/* Role-specific */}
        <div className="bg-white border border-[#E2DDD4] p-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-5">
            {profile.role === "student" ? "Academic Info" : profile.role === "faculty" ? "Employment Details" : "Additional Info"}
          </p>
          <div className="space-y-5">
            {(roleSpecificFields[profile.role] || []).map(({ key, label }) => (
              <div key={key}>
                <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#BDBAA8] mb-1.5">{label}</label>
                {editing ? (
                  <input value={form[key] || ""} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
                ) : (
                  <p className="font-['DM_Sans'] text-sm text-[#0C0C10] pb-2 border-b border-[#F0ECE4]">{profile[key] || <span className="text-[#BDBAA8]">Not set</span>}</p>
                )}
              </div>
            ))}
            <div>
              <label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#BDBAA8] mb-2">Bio / Notes</label>
              {editing ? (
                <textarea value={form.bio || ""} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} rows={3} placeholder="Add a short bio..."
                  className="w-full bg-transparent border border-[#D8D4CB] p-3 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] resize-none transition-colors" />
              ) : (
                <p className="font-['DM_Sans'] text-sm text-[#7A7468]">{profile.bio || <span className="text-[#BDBAA8]">No bio added.</span>}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Parent child linking */}
      <div className="bg-white border border-[#E2DDD4] p-6">
        <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Child Account Linking</p>
        {child ? (
          <div className="flex items-center gap-4 p-4 border border-[#0A7AFF]/20 bg-[#0A7AFF]/5">
            <div className="w-12 h-12 bg-[#0A7AFF]/15 flex items-center justify-center flex-shrink-0">
              <span className="font-['Syne'] font-bold text-lg text-[#0A7AFF]">{child.name?.charAt(0)}</span>
            </div>
            <div>
              <p className="font-['Syne'] font-bold text-sm text-[#0C0C10]">{child.name}</p>
              <p className="font-['DM_Sans'] text-xs text-[#7A7468]">{child.email}</p>
              <p className="font-['DM_Sans'] text-xs text-[#22C55E] font-medium mt-0.5">Attendance: {child.attendance_percentage}%</p>
            </div>
            <button onClick={() => setChild(null)} className="ml-auto font-['Syne'] text-[9px] font-bold uppercase tracking-wider text-[#FF3636] border border-[#FF3636]/30 px-3 py-2 hover:bg-[#FF3636]/8 transition-colors">Unlink</button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <input value={childEmail} onChange={e => setChildEmail(e.target.value)} placeholder="Enter student email to link (e.g. student@acadence.edu)"
                className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" />
              {linkMsg && <p className={`font-['DM_Sans'] text-xs mt-1 ${linkMsg.startsWith("Linked") ? "text-[#22C55E]" : "text-[#FF3636]"}`}>{linkMsg}</p>}
            </div>
            <button onClick={handleLinkChild} disabled={linking || !childEmail} data-testid="link-child-btn"
              className="flex items-center gap-2 bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors disabled:opacity-50">
              {linking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Link2 className="w-3.5 h-3.5" /> Link</>}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
